import { supabase, isDevelopmentMode } from '../utils/supabase/client';
import { dataService } from './dataService';
import React from 'react';

/**
 * 🔄 SERVICE DE RAFRAICHISSEMENT AUTOMATIQUE DES DONNÉES
 * Met à jour toutes les statistiques et données de l'application
 */

class DataRefreshService {
  constructor() {
    this.refreshIntervalId = null;
    this.isRefreshing = false;
    this.lastRefreshTime = null;
  }

  // ============================================
  // 🎯 RAFRAICHISSEMENT PRINCIPAL
  // ============================================

  /**
   * Lance le rafraîchissement automatique de toutes les données
   */
  async refreshAllData(forceRefresh = false) {
    if (this.isRefreshing && !forceRefresh) {
      console.log('⏳ Rafraîchissement déjà en cours...');
      return { success: false, skipped: true, reason: 'already_refreshing' };
    }

    this.isRefreshing = true;
    console.log('🔄 Début du rafraîchissement automatique des données...');

    try {
      // 1. Rafraîchir les projets et statistiques
      await this.refreshProjectsData();
      
      // 2. Rafraîchir les données utilisateurs
      await this.refreshUsersData();
      
      // 3. Rafraîchir les investissements
      await this.refreshInvestmentsData();
      
      // 4. Rafraîchir les statistiques globales
      await this.refreshGlobalStats();
      
      // 5. Synchroniser avec localStorage
      await this.syncLocalStorage();
      
      // 6. Émettre l'événement de mise à jour
      this.emitDataUpdated();
      
      this.lastRefreshTime = new Date();
      console.log('✅ Rafraîchissement terminé avec succès');
      
      return { success: true, timestamp: this.lastRefreshTime };
    } catch (error) {
      console.error('❌ Erreur lors du rafraîchissement:', error);
      return { success: false, error: error.message };
    } finally {
      this.isRefreshing = false;
    }
  }

  // ============================================
  // 📊 RAFRAICHISSEMENT PAR CATÉGORIE
  // ============================================

  /**
   * Met à jour les données des projets
   */
  async refreshProjectsData() {
    try {
      console.log('🔄 Mise à jour des projets...');

      // Mode développement : pas de Supabase configuré, on reste sur les données statiques
      if (isDevelopmentMode) {
        return this.getStaticProjectStats();
      }
      
      // Récupérer tous les projets depuis Supabase
      const projectsResult = await dataService.project.getAllProjects();
      
      if (projectsResult.success && projectsResult.data) {
        // Calculer les statistiques des projets
        const stats = this.calculateProjectStats(projectsResult.data);
        
        // Mettre à jour localStorage
        localStorage.setItem('projects', JSON.stringify(projectsResult.data));
        localStorage.setItem('projectStats', JSON.stringify(stats));
        
        console.log(`✅ ${projectsResult.data.length} projets mis à jour`);
        return stats;
      } else {
        // Fallback sur données statiques
        const staticStats = this.getStaticProjectStats();
        localStorage.setItem('projectStats', JSON.stringify(staticStats));
        return staticStats;
      }
    } catch (error) {
      console.error('❌ Erreur rafraîchissement projets:', error);
      return this.getStaticProjectStats();
    }
  }

  /**
   * Met à jour les données des utilisateurs
   */
  async refreshUsersData() {
    try {
      console.log('🔄 Mise à jour des données utilisateurs...');

      // Mode développement : pas de Supabase configuré, on reste sur les données statiques
      if (isDevelopmentMode) {
        return this.getStaticUserStats();
      }
      // Ne pas interroger Supabase si l'utilisateur n'est pas connecté (évite les requêtes annulées/401)
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.warn('⚠️ Session Supabase indisponible:', sessionError.message);
      }
      const session = sessionData?.session;
      if (!session) {
        // En mode non authentifié, utiliser des stats statiques et éviter la requête réseau
        return this.getStaticUserStats();
      }
      
      // Récupérer le nombre total d'utilisateurs
      const { count: usersCount, error } = await supabase
        .from('user_profiles')
        // Sélection minimale pour le comptage afin d'éviter un select global inutile
        .select('id', { count: 'exact', head: true });

      if (!error) {
        const userStats = {
          totalUsers: usersCount || 0,
          activeInvestors: Math.floor((usersCount || 0) * 0.7), // 70% sont des investisseurs actifs
          newUsersThisMonth: Math.floor((usersCount || 0) * 0.1)
        };
        
        localStorage.setItem('userStats', JSON.stringify(userStats));
        console.log(`✅ ${usersCount} utilisateurs recensés`);
        return userStats;
      } else {
        return this.getStaticUserStats();
      }
    } catch (error) {
      console.error('❌ Erreur rafraîchissement utilisateurs:', error);
      return this.getStaticUserStats();
    }
  }

  /**
   * Met à jour les données des investissements
   */
  async refreshInvestmentsData() {
    try {
      console.log('🔄 Mise à jour des investissements...');

      // Mode développement : pas de Supabase configuré, on reste sur les données statiques
      if (isDevelopmentMode) {
        return this.getStaticInvestmentStats();
      }
      
      // Récupérer les statistiques d'investissements
      const { data: investments, error } = await supabase
        .from('investments')
        .select('amount, expected_return, status')
        .eq('status', 'confirmed');

      if (!error && investments) {
        const investmentStats = {
          totalInvestments: investments.length,
          totalAmount: investments.reduce((sum, inv) => sum + (parseFloat(inv.amount) || 0), 0),
          averageReturn: investments.length > 0 
            ? investments.reduce((sum, inv) => sum + (parseFloat(inv.expected_return) || 0), 0) / investments.length 
            : 0,
          successRate: 0.85 // 85% de taux de réussite
        };
        
        localStorage.setItem('investmentStats', JSON.stringify(investmentStats));
        console.log(`✅ ${investments.length} investissements traités`);
        return investmentStats;
      } else {
        return this.getStaticInvestmentStats();
      }
    } catch (error) {
      console.error('❌ Erreur rafraîchissement investissements:', error);
      return this.getStaticInvestmentStats();
    }
  }

  /**
   * Met à jour les statistiques globales
   */
  async refreshGlobalStats() {
    try {
      console.log('🔄 Calcul des statistiques globales...');
      
      const projectStats = JSON.parse(localStorage.getItem('projectStats') || '{}');
      const userStats = JSON.parse(localStorage.getItem('userStats') || '{}');
      const investmentStats = JSON.parse(localStorage.getItem('investmentStats') || '{}');
      
      const globalStats = {
        // Projets disponibles
        availableProjects: projectStats.totalProjects || 250,
        
        // Financement moyen (pourcentage)
        averageFunding: projectStats.averageFundingPercentage || 65,
        
        // Investisseurs actifs
        activeInvestors: userStats.activeInvestors || 1200,
        
        // Retour moyen (pourcentage)
        averageReturn: Math.round((investmentStats.averageReturn || 0.08) * 100),
        
        // Autres statistiques utiles
        totalInvested: investmentStats.totalAmount || 500000000, // 500 M FCFA
        successRate: Math.round((investmentStats.successRate || 0.85) * 100),
        
        // Métadonnées
        lastUpdated: new Date().toISOString(),
        dataSource: 'supabase_with_fallback'
      };
      
      // Sauvegarder dans localStorage
      localStorage.setItem('globalStats', JSON.stringify(globalStats));
      
      console.log('✅ Statistiques globales calculées:', globalStats);
      return globalStats;
    } catch (error) {
      console.error('❌ Erreur calcul statistiques globales:', error);
      return this.getStaticGlobalStats();
    }
  }

  // ============================================
  // 📊 CALCULS DE STATISTIQUES
  // ============================================

  calculateProjectStats(projects) {
    if (!projects || projects.length === 0) {
      return this.getStaticProjectStats();
    }

    const totalProjects = projects.length;
    const totalTarget = projects.reduce((sum, p) => sum + (p.target_amount || p.coutTotal || 0), 0);
    const totalRaised = projects.reduce((sum, p) => sum + (p.raised_amount || p.montantRecu || 0), 0);
    const totalInvestors = projects.reduce((sum, p) => sum + (p.investors_count || p.membres || 0), 0);
    
    return {
      totalProjects,
      totalTargetAmount: totalTarget,
      totalRaisedAmount: totalRaised,
      totalInvestors,
      averageFundingPercentage: totalTarget > 0 ? Math.round((totalRaised / totalTarget) * 100) : 0,
      averageProjectSize: totalProjects > 0 ? Math.round(totalTarget / totalProjects) : 0
    };
  }

  // ============================================
  // 🔄 SYNCHRONISATION LOCALSTORAGE
  // ============================================

  async syncLocalStorage() {
    try {
      console.log('🔄 Synchronisation localStorage...');
      
      // Récupérer toutes les données actuelles
      const globalStats = JSON.parse(localStorage.getItem('globalStats') || '{}');
      
      // Mettre à jour les données dans différents formats pour compatibilité
      const legacyStats = {
        projects: globalStats.availableProjects || 250,
        funding: globalStats.averageFunding || 65,
        investors: globalStats.activeInvestors || 1200,
        returns: globalStats.averageReturn || 8
      };
      
      localStorage.setItem('legacyStats', JSON.stringify(legacyStats));
      localStorage.setItem('lastDataRefresh', new Date().toISOString());
      
      console.log('✅ Synchronisation localStorage terminée');
    } catch (error) {
      console.error('❌ Erreur synchronisation localStorage:', error);
    }
  }

  // ============================================
  // 📡 GESTION DES ÉVÉNEMENTS
  // ============================================

  emitDataUpdated() {
    // Émettre un événement personnalisé pour notifier les composants
    const event = new CustomEvent('dataRefreshed', {
      detail: {
        timestamp: new Date().toISOString(),
        stats: JSON.parse(localStorage.getItem('globalStats') || '{}')
      }
    });
    window.dispatchEvent(event);
  }

  // ============================================
  // 🔧 RAFRAICHISSEMENT AUTOMATIQUE
  // ============================================

  startAutoRefresh(intervalMinutes = 30) {
    console.log(`🚀 Démarrage du rafraîchissement automatique (${intervalMinutes} min)`);
    
    // Arrêter l'ancien timer s'il existe
    this.stopAutoRefresh();
    
    // Premier rafraîchissement immédiat
    this.refreshAllData();
    
    // Programmer les rafraîchissements suivants
    this.refreshIntervalId = setInterval(() => {
      this.refreshAllData();
    }, intervalMinutes * 60 * 1000);
  }

  stopAutoRefresh() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
      console.log('⏹️ Rafraîchissement automatique arrêté');
    }
  }

  // ============================================
  // 📊 DONNÉES STATIQUES DE FALLBACK
  // ============================================

  getStaticProjectStats() {
    return {
      totalProjects: 250,
      totalTargetAmount: 1000000000,
      totalRaisedAmount: 650000000,
      totalInvestors: 1200,
      averageFundingPercentage: 65,
      averageProjectSize: 4000000
    };
  }

  getStaticUserStats() {
    return {
      totalUsers: 1500,
      activeInvestors: 1200,
      newUsersThisMonth: 150
    };
  }

  getStaticInvestmentStats() {
    return {
      totalInvestments: 3500,
      totalAmount: 500000000,
      averageReturn: 0.08,
      successRate: 0.85
    };
  }

  getStaticGlobalStats() {
    return {
      availableProjects: 250,
      averageFunding: 65,
      activeInvestors: 1200,
      averageReturn: 8,
      totalInvested: 500000000,
      successRate: 85,
      lastUpdated: new Date().toISOString(),
      dataSource: 'static_fallback'
    };
  }

  // ============================================
  // 🔍 MÉTHODES UTILITAIRES
  // ============================================

  getLastRefreshTime() {
    return this.lastRefreshTime;
  }

  isCurrentlyRefreshing() {
    return this.isRefreshing;
  }

  async forceRefresh() {
    console.log('🔄 Rafraîchissement forcé demandé...');
    return await this.refreshAllData(true);
  }

  // Récupérer les statistiques actuelles
  getCurrentStats() {
    return JSON.parse(localStorage.getItem('globalStats') || '{}');
  }

  // Vérifier si les données sont récentes (moins de 1h)
  isDataFresh() {
    const lastUpdate = localStorage.getItem('lastDataRefresh');
    if (!lastUpdate) return false;
    
    const oneHourAgo = new Date().getTime() - (60 * 60 * 1000);
    return new Date(lastUpdate).getTime() > oneHourAgo;
  }
}

// Instance singleton
const dataRefreshService = new DataRefreshService();

// ============================================
// 🚀 EXPORTS ET API PUBLIQUE
// ============================================

export default dataRefreshService;

// Fonctions d'aide pour les composants
export const refreshAllData = () => dataRefreshService.refreshAllData();
export const forceRefreshData = () => dataRefreshService.forceRefresh();
export const startAutoRefresh = (minutes = 30) => dataRefreshService.startAutoRefresh(minutes);
export const stopAutoRefresh = () => dataRefreshService.stopAutoRefresh();
export const getCurrentStats = () => dataRefreshService.getCurrentStats();
export const isDataFresh = () => dataRefreshService.isDataFresh();

// Hook React pour utiliser le service dans les composants
export const useDataRefresh = () => {
  const [stats, setStats] = React.useState(dataRefreshService.getCurrentStats());
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  
  React.useEffect(() => {
    const handleDataRefresh = (event) => {
      setStats(event.detail.stats);
      setIsRefreshing(false);
    };
    
    window.addEventListener('dataRefreshed', handleDataRefresh);
    
    // Vérifier si les données sont fraîches au montage
    if (!dataRefreshService.isDataFresh()) {
      setIsRefreshing(true);
      dataRefreshService.refreshAllData().then(() => {
        setIsRefreshing(false);
      });
    }
    
    return () => {
      window.removeEventListener('dataRefreshed', handleDataRefresh);
    };
  }, []);
  
  const refresh = React.useCallback(() => {
    setIsRefreshing(true);
    return dataRefreshService.forceRefresh().then(() => {
      setIsRefreshing(false);
    });
  }, []);
  
  return {
    stats,
    isRefreshing,
    refresh,
    lastRefreshTime: dataRefreshService.getLastRefreshTime()
  };
};